import { apiFetch } from '../utils/apiFetch';
import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface WhitelabelBranding {
  hasWhitelabel: boolean;
  companyName: string;
  companyLogo: string | null;
}

interface WhitelabelContextType {
  branding: WhitelabelBranding;
  isLoading: boolean;
  refreshBranding: () => Promise<void>;
}

const WhitelabelContext = createContext<WhitelabelContextType | undefined>(undefined);

export const useWhitelabel = () => {
  const context = useContext(WhitelabelContext);
  if (!context) {
    // Return default values instead of throwing error
    // This prevents blocking if provider isn't set up correctly
    return {
      branding: {
        hasWhitelabel: false,
        companyName: 'Optimancer AIO',
        companyLogo: null,
      },
      isLoading: false,
      refreshBranding: async () => {},
    };
  }
  return context;
};

interface WhitelabelProviderProps {
  children: ReactNode;
}

export const WhitelabelProvider = ({ children }: WhitelabelProviderProps) => {
  const [branding, setBranding] = useState<WhitelabelBranding>({
    hasWhitelabel: false,
    companyName: 'Optimancer AIO',
    companyLogo: null,
  });
  const [isLoading, setIsLoading] = useState(false);

  const fetchBranding = async () => {
    try {
      setIsLoading(true);
      const response = await apiFetch('/api/whitelabel/branding', {
        credentials: 'include',
      });

      if (!response.ok) {
        console.warn('Failed to fetch branding, using defaults');
        return;
      }

      const data = await response.json();
      setBranding(data);
    } catch (error) {
      console.error('Failed to fetch whitelabel branding:', error);
      // Use defaults on error - don't block the app
      setBranding({
        hasWhitelabel: false,
        companyName: 'Optimancer AIO',
        companyLogo: null,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBranding();
  }, []);

  // Sync the browser tab title with the active brand so whitelabel customers
  // don't see "Optimancer" in their tab. The HTML <title> is intentionally
  // generic ("Platform") so this effect only ever upgrades it.
  useEffect(() => {
    if (branding.companyName) {
      document.title = `${branding.companyName} Platform`;
    }
  }, [branding.companyName]);

  const refreshBranding = async () => {
    setIsLoading(true);
    await fetchBranding();
  };

  return (
    <WhitelabelContext.Provider value={{ branding, isLoading, refreshBranding }}>
      {children}
    </WhitelabelContext.Provider>
  );
};
